<template>
  <div class="rmr-page-shell pbv-page">
    <rmr-page-hero
      title="Créer un article via"
      title-accent="transcription vidéo"
      subtitle="Une URL YouTube, Instagram, TikTok ou X : la transcription devient un ou plusieurs articles, avec la vidéo embarquée au bon time code."
      :icon="icons.mdiVideoOutline"
    >
      <template #actions>
        <rmr-icon-button
          :icon="icons.mdiHelpCircleOutline"
          variant="edit"
          size="sm"
          tooltip="Guide d'utilisation"
          @click="helpOpen = true"
        />
        <rmr-action-button
          v-if="video.state.source"
          label="Nouvelle vidéo"
          :icon="icons.mdiRefresh"
          variant="translucent"
          size="sm"
          @click="video.reset"
        />
      </template>
    </rmr-page-hero>

    <section class="rmr-page-shell__content pbv-page__content">
      <video-scope-banner :video="video" @analyze="onAnalyze" />

      <rmr-notice
        v-if="!video.selectedSiteId.value"
        variant="info"
        class="mt-4"
        text="Sélectionnez un site pour lancer une analyse."
      />

      <rmr-notice
        v-if="video.state.error"
        variant="warning"
        class="mt-4"
        :text="video.state.error"
      />

      <template v-if="video.state.source">
        <source-preview-card :video="video" class="mt-4" />

        <v-card
          v-if="video.state.angles.length"
          flat
          class="rmr-table-shell mt-4"
        >
          <rmr-table-header>
            <template #heading>
              <div class="rmr-table-shell__eyebrow">Étape 2</div>
              <div class="rmr-table-shell__title">Angles proposés</div>
              <div class="pbv-page__count">{{ anglesCountLabel }}</div>
            </template>
            <template #controls>
              <rmr-action-button
                label="Proposer d'autres angles"
                :icon="icons.mdiAutorenew"
                variant="ghost"
                size="sm"
                :loading="video.state.proposing"
                :disabled="!video.canPropose.value"
                @click="video.proposeMoreAngles"
              />
            </template>
          </rmr-table-header>

          <v-card-text class="rmr-table-shell__body">
            <video-angle-list
              :value="video.state.angles"
              @add="video.addAngle"
              @remove="video.removeAngle"
            />
          </v-card-text>
        </v-card>
      </template>

      <!--
        Barre flottante partagee avec la veille globale. `show-options` : le
        menu Options (template, statut, titre force) doit etre la aussi en mode
        angles, ce que la veille ne fait pas.
      -->
      <generation-action-bar
        v-if="video.state.angles.length"
        mode="angles"
        clear-label="Tout décocher"
        :count="video.selectedAngles.value.length"
        :site-id="video.selectedSiteId.value || 0"
        :busy="video.state.sending"
        :progress="video.sendProgress.value"
        :primary-disabled="!video.canSubmit.value"
        :show-options="true"
        @clear="video.clearSelection"
        @primary="video.submit"
      >
        <template #options>
          <generation-options-menu
            :site-id="video.selectedSiteId.value || 0"
            :has-extra-options="video.state.topicIsTitle"
          >
            <div class="gw-options__block gw-options__block--legacy">
              <label class="rmr-table-shell__filter-label">
                Statut de publication
              </label>
              <status-filter store="aiFactory" dense />
            </div>
            <rmr-toggle-card
              :value="video.state.topicIsTitle"
              label="Forcer l'angle proposé comme titre"
              size="sm"
              @input="video.state.topicIsTitle = $event"
            />
          </generation-options-menu>
        </template>
      </generation-action-bar>

      <video-help-modal v-model="helpOpen" />

      <v-snackbar
        v-model="video.state.snackbar.show"
        :color="video.state.snackbar.color"
        :timeout="video.state.snackbar.timeout"
        top
      >
        <strong>{{ video.state.snackbar.message }}</strong>

        <template v-slot:action>
          <rmr-action-button
            v-for="action in video.state.snackbar.actions"
            :key="action.text"
            :label="action.text"
            variant="translucent"
            size="sm"
            @click="action.onClick()"
          />
        </template>
      </v-snackbar>
    </section>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
  mdiAutorenew,
  mdiHelpCircleOutline,
  mdiRefresh,
  mdiVideoOutline,
} from "@mdi/js";

import StatusFilter from "@/components/ai-factory/full-auto/filters/StatusFilter.vue";
import GenerationActionBar from "@/components/ai-factory/post-by-sources/global-watch/GenerationActionBar.vue";
import GenerationOptionsMenu from "@/components/ai-factory/post-by-sources/global-watch/GenerationOptionsMenu.vue";
import SourcePreviewCard from "@/components/ai-factory/post-by-video/SourcePreviewCard.vue";
import VideoAngleList from "@/components/ai-factory/post-by-video/VideoAngleList.vue";
import VideoHelpModal from "@/components/ai-factory/post-by-video/VideoHelpModal.vue";
import VideoScopeBanner from "@/components/ai-factory/post-by-video/VideoScopeBanner.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";

import useVideoTranscription from "@/components/ai-factory/post-by-video/useVideoTranscription";

export default {
  name: "GeneratePostByVideo",
  components: {
    GenerationActionBar,
    GenerationOptionsMenu,
    RmrActionButton,
    RmrIconButton,
    RmrNotice,
    RmrPageHero,
    RmrTableHeader,
    RmrToggleCard,
    SourcePreviewCard,
    StatusFilter,
    VideoAngleList,
    VideoHelpModal,
    VideoScopeBanner,
  },
  setup() {
    const video = useVideoTranscription();
    const helpOpen = ref(false);

    // =>  COMPUTED
    const anglesCountLabel = computed(() => {
      const total = video.state.angles.length;
      const selected = video.selectedAngles.value.length;
      return `${selected} / ${total} angle(s) retenu(s)`;
    });

    // =>  METHODS
    const onAnalyze = () => {
      if (video.canAnalyze.value) video.analyze();
    };

    // =>  DATA
    return {
      anglesCountLabel,
      helpOpen,
      icons: {
        mdiAutorenew,
        mdiHelpCircleOutline,
        mdiRefresh,
        mdiVideoOutline,
      },
      onAnalyze,
      video,
    };
  },
};
</script>

<style lang="scss" scoped>
// Room for the fixed action bar so the last angle is never hidden behind it.
.pbv-page__content {
  padding-bottom: 96px;
}

.pbv-page__count {
  margin-top: 4px;
  font-size: 12.5px;
  color: $rmr-text-subtle;
}
</style>
